import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Radio,
  RadioGroup,
  Divider,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { FactSection, TenderingFactMapping } from '../../models/tendering';
import { deepCloneObject } from '../../utils/clone';

interface SectionEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (section: FactSection) => void;
  section: FactSection;
}

const SectionEditDialog: React.FC<SectionEditDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  section: initialSection,
}) => {
  const [section, setSection] = useState<FactSection | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSection(deepCloneObject(initialSection));
    }
  }, [isOpen, initialSection]);

  const updateSectionName = (name: string) => {
    setSection((prev) => ({ ...prev!, name }));
  };

  const updateMapping = (index: number, updates: Partial<TenderingFactMapping>) => {
    setSection((prev) => {
      if (!prev) return prev;
      const updatedMappings = [...(prev.mappings || [])];
      updatedMappings[index] = { ...updatedMappings[index], ...updates };
      return { ...prev, mappings: updatedMappings };
    });
  };

  const handleSave = () => {
    if (section) {
      onSave(section);
    }
  };

  if (!section) return null;

  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            {section.name}
          </DialogTitle>
          <DialogContent className="max-h-[70vh] overflow-y-auto space-y-4">
            {/* Section Name */}
            <div>
              <h5 className="text-sm font-semibold mb-2">Namn på sektion:</h5>
              <Input
                value={section.name}
                onChange={(e) => updateSectionName(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Mappings */}
            <div>
              <h4 className="text-base font-semibold mb-4">Kopplade frågor:</h4>
              {section.mappings && section.mappings.length > 0 ? (
                <div className="space-y-4">
                  {section.mappings.map((mapping, index) => (
                    <div key={index}>
                      <div className="space-y-3 p-4 border border-gray-300 rounded">
                        <div>
                          <h5 className="text-sm font-semibold mb-2">Rubrik:</h5>
                          <Input
                            value={mapping.heading || ''}
                            onChange={(e) => updateMapping(index, { heading: e.target.value })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <h5 className="text-sm font-semibold mb-2">Text:</h5>
                          <Input
                            value={mapping.text || ''}
                            onChange={(e) => updateMapping(index, { text: e.target.value })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <RadioGroup
                            value={mapping.permission || 'approval'}
                            onChange={(_, data) => updateMapping(index, { permission: data.value })}
                          >
                            <Radio value="approval" label="Kräver godkännande" />
                            <Radio value="editable" label="Direkt ändringsbart" />
                            <Radio value="locked" label="Ej ändringsbart" />
                          </RadioGroup>
                        </div>
                      </div>
                      {index < section.mappings!.length - 1 && <Divider className="my-4" />}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">Inga frågor kopplade än</p>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Avbryt
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              OK
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SectionEditDialog;
